import React, { useState } from 'react';
import { useBilling } from '../../contexts/BillingContext';
import { CatalogueItem } from '../../types/billing';
import { calculateLine, formatMoney, taxFields, taxFor } from '../../utils/billingMoney';
import { Dialog } from '../../components/ui/Dialog';
import { Button } from '../../components/ui/Button';
type Props = {
  item?: CatalogueItem;
  onClose: () => void;
  onSaved: (message?: string) => void;
};
type Tab = 'item' | 'inventory';
const input = 'block w-full rounded-sm border border-slate-400 px-2 py-1.5 text-sm placeholder:text-slate-400 focus:border-sky-600 focus:outline-none focus:ring-1 focus:ring-sky-600 disabled:bg-slate-100';
const chargeTypes: {
  id: CatalogueItem['sourceType'];
  label: string;
}[] = [{
  id: 'consultation',
  label: 'Consultation'
}, {
  id: 'lab',
  label: 'Laboratory test'
}, {
  id: 'radiology',
  label: 'Radiology scan'
}, {
  id: 'procedure',
  label: 'Procedure'
}, {
  id: 'pharmacy',
  label: 'Pharmacy'
}, {
  id: 'other',
  label: 'Other'
}];

/** Label-left form row, as in the Express Invoice item dialog. */
function Row({
  id,
  label,
  children,
  top
}: {
  id: string;
  label: string;
  children: React.ReactNode;
  top?: boolean;
}) {
  return <div className={`grid gap-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] sm:gap-4 ${top ? 'sm:items-start' : 'sm:items-center'}`} data-id="element-3633" data-mp="2x3njc2au6cp3">
      <label htmlFor={id} className={`text-sm text-slate-800 ${top ? 'sm:pt-1.5' : ''}`} data-id="element-3634" data-mp="321ozofgvnxwv">{label}</label>
      <div data-id="element-3635" data-mp="2o1xsoyos3hsd">{children}</div>
    </div>;
}

/** Add or edit an item. Every item is listed in Inventory; stock items also carry quantities. */
export function ItemFormModal({
  item,
  onClose,
  onSaved
}: Props) {
  const {
    config,
    saveItem
  } = useBilling();
  const fmt = (v: number) => formatMoney(v, config.regional.currencySymbol);
  const [tab, setTab] = useState<Tab>('item');
  const [help, setHelp] = useState(false);
  const [form, setForm] = useState({
    code: item?.code ?? '',
    name: item?.name ?? '',
    description: item?.description ?? '',
    category: item?.category ?? config.catalogue.categories[0] ?? '',
    department: item?.department ?? config.catalogue.departments[0] ?? '',
    kind: (item?.kind ?? (item ? 'Service' : 'Stock')) as 'Service' | 'Stock',
    heldIn: (item?.medicineId ? 'pharmacy' : 'billing') as 'billing' | 'pharmacy',
    unit: item?.unit ?? config.catalogue.units[0] ?? '',
    medicineId: item?.medicineId ?? '',
    unitPrice: item ? String(item.unitPrice) : '',
    taxId: item?.taxId ?? config.taxes.defaultTaxId,
    taxInclusive: item?.taxInclusive === undefined ? 'default' : item.taxInclusive ? 'yes' : 'no',
    discountAllowed: item?.discountAllowed ?? true,
    sourceType: item?.sourceType ?? 'other' as CatalogueItem['sourceType'],
    matchNames: (item?.matchNames ?? []).join(', '),
    openingQuantity: '',
    reorderLevel: item?.reorderLevel !== undefined ? String(item.reorderLevel) : '',
    costPrice: item?.costPrice !== undefined ? String(item.costPrice) : '',
    supplier: item?.supplier ?? '',
    location: item?.location ?? ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = <K extends keyof typeof form,>(k: K, v: (typeof form)[K]) => setForm(f => ({
    ...f,
    [k]: v
  }));
  const taxes = config.taxes.rates.filter(t => t.active || t.id === form.taxId);
  const defaultTax = taxFor(config);
  const isStock = form.kind === 'Stock';
  /** Billing inventory is independent of Pharmacy — items are never linked to medicines. */
  const linked = false;
  const inclusive = form.taxInclusive === 'default' ? config.taxes.pricesIncludeTax : form.taxInclusive === 'yes';
  const preview = calculateLine({
    id: 'p',
    description: '',
    quantity: 1,
    unitPrice: Number(form.unitPrice) || 0,
    discountPct: 0,
    ...taxFields(taxFor(config, form.taxId)),
    taxInclusive: inclusive
  });
  const submit = async () => {
    setError('');
    const num = (v: string) => v.trim() === '' ? undefined : Number(v);
    setSaving(true);
    const result = await saveItem({
      id: item?.id,
      code: form.code,
      name: form.name,
      description: form.description.trim() || undefined,
      category: form.category,
      department: form.department,
      kind: form.kind,
      unit: form.unit,
      medicineId: undefined,
      medicineName: undefined,
      unitPrice: Number(form.unitPrice),
      taxId: form.taxId,
      taxInclusive: form.taxInclusive === 'default' ? undefined : form.taxInclusive === 'yes',
      discountAllowed: form.discountAllowed,
      sourceType: linked ? 'pharmacy' : form.sourceType,
      matchNames: form.matchNames.split(','),
      status: item?.status ?? 'Active',
      legacyRef: item?.legacyRef,
      reorderLevel: isStock && !linked ? num(form.reorderLevel) : undefined,
      costPrice: num(form.costPrice),
      supplier: form.supplier.trim() || undefined,
      location: isStock && !linked ? form.location.trim() || undefined : undefined,
      openingQuantity: isStock && !linked ? Number(form.openingQuantity) || 0 : 0
    });
    setSaving(false);
    if (!result.ok) {
      const msg = result.error ?? 'The item was not saved.';
      if (/reorder|cost|opening/i.test(msg)) setTab('inventory');else if (/code|name|price|tax|category|department/i.test(msg)) setTab('item');
      return setError(msg);
    }
    onSaved(result.message);
  };
  const tabs: {
    id: Tab;
    label: string;
  }[] = [{
    id: 'item',
    label: 'Item'
  }, {
    id: 'inventory',
    label: 'Inventory'
  }];
  return <Dialog title={item ? `Item — ${item.code}` : 'Item'} onClose={onClose} size="lg" footer={<>
          {error && <p role="alert" className="mr-auto text-sm text-red-700" data-id="element-3637" data-mp="1ks4nuira9apa">{error}</p>}
          <Button onClick={submit} isLoading={saving} className="min-w-[88px]" data-id="element-3638" data-mp="172atxm0x808p">OK</Button>
          <Button variant="outline" onClick={onClose} className="min-w-[88px]" data-id="element-3639" data-mp="1b2ut0v521dxh">Cancel</Button>
          <Button variant="outline" onClick={() => setHelp(h => !h)} aria-expanded={help} className="min-w-[88px]" data-id="element-3640" data-mp="1yt77ypypwaxk">Help</Button>
        </>} data-id="element-3636" data-mp="1ftky079kjern">
      <div role="tablist" aria-label="Item details" className="-mb-px flex gap-0.5" data-id="element-3641" data-mp="23l8ia6cbrrky">
        {tabs.map(t => <button key={t.id} type="button" role="tab" id={`itab-${t.id}`} aria-selected={tab === t.id} aria-controls={`ipanel-${t.id}`} onClick={() => setTab(t.id)} className={`rounded-t-sm border px-3 py-1 text-sm ${tab === t.id ? 'relative z-10 border-slate-300 border-b-white bg-white font-medium text-slate-900' : 'border-slate-300 bg-slate-100 text-slate-700 hover:bg-slate-50'}`} data-id="element-3642" data-mp="2deuswdcqvjhh">
            {t.label}
          </button>)}
      </div>

      <div className="rounded-sm border border-slate-300 bg-white p-4 sm:p-5" data-id="element-3643" data-mp="1pp0j64nq2b1g">
        {tab === 'item' ? <div id="ipanel-item" role="tabpanel" aria-labelledby="itab-item" className="space-y-3" data-id="element-3644" data-mp="1lp9ldgzs2unf">
            <Row id="it-code" label="Item:" data-id="element-3645" data-mp="1gqoitax1zyvt"><input id="it-code" value={form.code} onChange={e => set('code', e.target.value.toUpperCase())} placeholder="eg. WID2" className={input} autoFocus data-id="element-3646" data-mp="1gnaj6afry3s2" /></Row>
            <Row id="it-name" label="Name:" data-id="element-3647" data-mp="16siiuyut52tl"><input id="it-name" value={form.name} onChange={e => set('name', e.target.value)} placeholder="eg. Full blood count" className={input} data-id="element-3648" data-mp="16tada6co4uia" /></Row>
            <Row id="it-desc" label="Description:" top data-id="element-3649" data-mp="11lc5alptfrv1"><textarea id="it-desc" rows={3} value={form.description} onChange={e => set('description', e.target.value)} placeholder="[Enter full description here]" className={`${input} resize-y`} data-id="element-3650" data-mp="11ogbfomucxoc" /></Row>
            <Row id="it-price" label="Unit Price:" data-id="element-3651" data-mp="1bi6zisf2363i"><input id="it-price" type="number" min="0" step="0.01" value={form.unitPrice} onChange={e => set('unitPrice', e.target.value)} placeholder={`eg. ${config.regional.currencySymbol}20.90`} className={`${input} tabular-nums`} data-id="element-3652" data-mp="dxkg4hj6fkcc" /></Row>
            <Row id="it-tax" label="Tax:" data-id="element-3653" data-mp="nsvv90yhytkm">
              <select id="it-tax" value={form.taxId} onChange={e => set('taxId', e.target.value)} className={`${input} bg-white`} data-id="element-3654" data-mp="sm76deacdwic">
                {taxes.map(t => <option key={t.id} value={t.id} data-id="element-3655" data-mp="3rbfh9lql136z">{t.id === defaultTax.id ? `Default — ${t.name}` : `${t.code} — ${t.name}`}{t.active ? '' : ' (inactive)'}</option>)}
              </select>
            </Row>
            <Row id="it-incl" label="Price includes tax:" data-id="element-3656" data-mp="1edxeet3le6zd">
              <select id="it-incl" value={form.taxInclusive} onChange={e => set('taxInclusive', e.target.value)} className={`${input} bg-white`} data-id="element-3657" data-mp="1abaurfh2rsd9">
                <option value="default" data-id="element-3658" data-mp="1o7o05q8c43bd">Default ({config.taxes.pricesIncludeTax ? 'inclusive' : 'exclusive'})</option><option value="yes" data-id="element-3659" data-mp="1jda9u28j4mdk">Yes — inclusive</option><option value="no" data-id="element-3660" data-mp="1x9xc8kj944qv">No — tax added on top</option>
              </select>
            </Row>
            <div className="border-t border-slate-200 pt-3" data-id="element-3661" data-mp="1t82ajexerpck" />
            <Row id="it-cat" label="Category:" data-id="element-3662" data-mp="270idygu81pe0"><select id="it-cat" value={form.category} onChange={e => set('category', e.target.value)} className={`${input} bg-white`} data-id="element-3663" data-mp="26zgrlg7r9ggz">{[...new Set([...config.catalogue.categories, form.category])].filter(Boolean).map(c => <option key={c} data-id="element-3664" data-mp="2gzhgdhoe8osk">{c}</option>)}</select></Row>
            <Row id="it-dept" label="Department:" data-id="element-3665" data-mp="2bzv3xage3ckt"><select id="it-dept" value={form.department} onChange={e => set('department', e.target.value)} className={`${input} bg-white`} data-id="element-3666" data-mp="2c0wdfahrbr0f">{[...new Set([...config.catalogue.departments, form.department])].filter(Boolean).map(c => <option key={c} data-id="element-3667" data-mp="2zm71bo45wdfi">{c}</option>)}</select></Row>
            {!linked && <Row id="it-charge" label="Hospital charge type:" data-id="element-3668" data-mp="2lm5rhj5x5vnw"><select id="it-charge" value={form.sourceType} onChange={e => set('sourceType', e.target.value as CatalogueItem['sourceType'])} className={`${input} bg-white`} data-id="element-3669" data-mp="2lpl5sjrrr0o9">{chargeTypes.map(c => <option key={c.id} value={c.id} data-id="element-3670" data-mp="2bvb2w857ajza">{c.label}</option>)}</select></Row>}
            {!linked && <Row id="it-match" label="Also matches (clinical names):" data-id="element-3671" data-mp="2gpfamtmaiw1f"><input id="it-match" value={form.matchNames} onChange={e => set('matchNames', e.target.value)} placeholder="FBC, Full Blood Count" className={input} data-id="element-3672" data-mp="22xim0zjtz49s" /></Row>}
            <p className="text-right text-xs text-slate-500" data-id="element-3673" data-mp="1syjly5d4sgye">One unit bills as {fmt(preview.total)} ({preview.taxName} {fmt(preview.tax)})</p>
          </div> : <div id="ipanel-inventory" role="tabpanel" aria-labelledby="itab-inventory" className="space-y-3" data-id="element-3674" data-mp="1xwuv0dvlqnfk">
            <Row id="iv-kind" label="Item type:" data-id="element-3675" data-mp="2olyvkj6d8cn">
              <select id="iv-kind" value={form.kind} onChange={e => set('kind', e.target.value as 'Service' | 'Stock')} className={`${input} bg-white`} data-id="element-3676" data-mp="7lvszdhie86t">
                <option value="Stock" data-id="element-3677" data-mp="3ow3qbzc0r293">Stock item — keep quantities</option>
                <option value="Service" data-id="element-3678" data-mp="3tt2iwdg1b156">Service — no stock to count</option>
              </select>
            </Row>
            <Row id="iv-unit" label="Unit of measure:" data-id="element-3679" data-mp="lf3kl55lb3oi"><select id="iv-unit" value={form.unit} onChange={e => set('unit', e.target.value)} className={`${input} bg-white`} data-id="element-3680" data-mp="qcd9mjp2hdbt">{[...new Set([...config.catalogue.units, form.unit])].filter(Boolean).map(c => <option key={c} data-id="element-3681" data-mp="glsghbs5x10a">{c}</option>)}</select></Row>
            {isStock && !linked && (item ? <Row id="iv-onhand" label="Quantity on hand:" data-id="element-3682" data-mp="14b35xx42b8z4">
                  <input id="iv-onhand" value={`${item.quantityOnHand ?? 0} ${form.unit}`} readOnly disabled className={input} data-id="element-3683" data-mp="192bih20ovagi" />
                  <p className="mt-1 text-xs text-slate-500" data-id="element-3684" data-mp="2trysfy03vyth">Change it with Receive stock or Adjust in Inventory, so every change is recorded.</p>
                </Row> : <Row id="iv-open" label="Opening quantity:" data-id="element-3685" data-mp="33j368ue1pdmq"><input id="iv-open" type="number" min="0" step="1" value={form.openingQuantity} onChange={e => set('openingQuantity', e.target.value)} placeholder="eg. 50" className={`${input} tabular-nums`} data-id="element-3686" data-mp="33i0i2u83xpai" /></Row>)}
            {isStock && !linked && <Row id="iv-reorder" label="Reorder level:" data-id="element-3687" data-mp="25w32b4l43y29"><input id="iv-reorder" type="number" min="0" step="1" value={form.reorderLevel} onChange={e => set('reorderLevel', e.target.value)} placeholder="eg. 10" className={`${input} tabular-nums`} data-id="element-3688" data-mp="25xe5p3anb3su" /></Row>}
            <Row id="iv-cost" label="Cost price:" data-id="element-3689" data-mp="3v9zgdn8q146b"><input id="iv-cost" type="number" min="0" step="0.01" value={form.costPrice} onChange={e => set('costPrice', e.target.value)} placeholder={`eg. ${config.regional.currencySymbol}12.50`} className={`${input} tabular-nums`} data-id="element-3690" data-mp="3v8y6vn7cspqp" /></Row>
            <Row id="iv-supplier" label="Supplier:" data-id="element-3691" data-mp="2nzky2q7u1x3a"><input id="iv-supplier" value={form.supplier} onChange={e => set('supplier', e.target.value)} className={input} data-id="element-3692" data-mp="2sw1wcd8qxru3" /></Row>
            {isStock && !linked && <Row id="iv-loc" label="Storage location:" data-id="element-3693" data-mp="2xtjwbe4scwpy"><input id="iv-loc" value={form.location} onChange={e => set('location', e.target.value)} placeholder="eg. Store room shelf B" className={input} data-id="element-3694" data-mp="31xro5k6jisse" /></Row>}
            <label className="flex items-center justify-end gap-2 text-sm text-slate-800" data-id="element-3695" data-mp="2555zv2tci8gi"><input type="checkbox" checked={form.discountAllowed} onChange={e => set('discountAllowed', e.target.checked)} className="rounded-sm border-slate-400" data-id="element-3696" data-mp="2a6bu552nm3s5" /> Discounts allowed on this item</label>
          </div>}
      </div>

      {help && <div className="mt-3 rounded-sm border border-sky-200 bg-sky-50 p-3 text-xs leading-relaxed text-sky-900" role="note" data-id="element-3697" data-mp="1mhapzu0uteso">
          <p data-id="element-3698" data-mp="1qgjmh6na3qrx"><span className="font-semibold" data-id="element-3699" data-mp="1qgjlb3bjlkqc">Item</span> is the short code shown on invoices (it must be unique). <span className="font-semibold" data-id="element-3700" data-mp="1iy1yjveno9ff">Unit Price</span> is the selling price; <span className="font-semibold" data-id="element-3701" data-mp="1e0jw8nv58sge">Tax</span> uses the default tax code unless you pick another.</p>
          <p className="mt-1" data-id="element-3702" data-mp="19wu6vw6na6ub">Every item appears in <span className="font-semibold" data-id="element-3703" data-mp="19zq8buhpbdni">Inventory</span>. Stock items are counted there and reduced automatically by cash sales. Billing inventory is kept separate from Pharmacy Inventory. Price changes never alter documents already issued.</p>
        </div>}
    </Dialog>;
}